import { CircularProgress, Switch, LinearProgress } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GAIT_MIN_STEPS } from "../../../../../constants/Demo/gaitAnalysis";
import { useGaitResultsActions, useUpdatingGaitResultsActions } from "../../../../../hooks/GaitAnalysis/actions";
import { GaitFeedbacks } from "../../../Feedbacks";
import BackendGaitAnalysis from "./BackendGaitAnalysis";
import GaitAnalysis from "./GaitAnalysis";
import GaitAnalysisFront from "./GaitAnalysisFront";
import BackendGaitAnalysisFront from "./BackendGaitAnalysisFront";
import LinearWithValueLabel from "./LinearProgress";
import { LoadingButton } from "@mui/lab";
import BackendVideoSelector from "./BackendVideoSelector";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import { getApiEndpoint, createApiRequest, BACKEND_CONFIG } from "../../../../../constants/backendConfig";
import { storage } from "../../../../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import VideocamIcon from "@mui/icons-material/Videocam";
import StopIcon from "@mui/icons-material/Stop";
import SecondaryCamera from "../../../SecondaryCamera";

const initialData = () => {
  return [
    {
      name: "Left Leg Angle",
      data: [],
    },
    {
      name: "Right Leg Angle",
      data: [],
    },
    {
      name: "Left Knee Angle",
      data: [],
    },
    {
      name: "Right knee Angle",
      data: [],
    },
    {
      name: "Left Ankle Angle",
      data: [],
    },
    {
      name: "Right Ankle Angle",
      data: [],
    },
    {
      name: "Left Hip Angle",
      data: [],
    },
    {
      name: "Right Hip Angle",
      data: [],
    },
    {
      name: "Hip Rotation Angle",
      data: [],
    },
    {
      name: "Hip Balance Angle",
      data: [],
    },
  ];
};

export default function BackendRecordingSide({ front, setFront }) {
  const [videoUrl, setVideoUrl] = useState("");
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState("");
  const [recordedVideoUrl, setRecordedVideoUrl] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [legAngleData, setLegAngleData] = React.useState(initialData());
  const sensorData = React.useRef({
    left: [],
    right: [],
  });
  const [buttonEnabled, setButtonEnabled] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [showWebcam, setShowWebcam] = React.useState(true);
  const [observationComponent, setObservationComponent] = React.useState(null);
  const [videoObservationComponent, setVideoObservationComponent] =
    React.useState(null);
  const [feedbackComponent, setFeedbackComponent] = React.useState(null);
  const [recordingEnabled, setRecordingEnabled] = React.useState(false);
  const [videoSaved, setVideoSaved] = React.useState(false);
  const [showSecondaryCamera, setShowSecondaryCamera] = React.useState(true);
  const { addResults, resetGaitResults } = useGaitResultsActions();
  const { updateResults } = useUpdatingGaitResultsActions();
  const [hasInitResult, setHasInitResult] = useState(false);
  const relativeLandmarks = React.useRef([]);
  const [useSensorForCycle, setUseSensorForCycle] = useState(false);
  const [apiProcessing, setApiProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [processingProgress, setProcessingProgress] = useState(0);
  
  // Video recording states
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);

  const gait = useSelector((state) =>
    front ? state.gaitFront.values : state.gait.values
  );
  const patientInfo = useSelector(({ gaitPatientInfo }) => gaitPatientInfo.values);
  const gaitResults = useSelector(({ gaitResults }) => gaitResults);
  const iter = useSelector(({ iter }) => iter.values);
  const [primaryDeviceId, setPrimaryDeviceId] = useState(null);
  const [secondaryDeviceId, setSecondaryDeviceId] = useState(null);

  const activeTrackingDeviceId = primaryDeviceId;

  useEffect(() => {
    if (front) {
      setLegAngleData(initialData());
    }
  }, [front]);

  useEffect(() => {
    if (iter?.cameraDeviceId && !primaryDeviceId) {
      setPrimaryDeviceId(iter.cameraDeviceId);
    }
  }, [iter?.cameraDeviceId, primaryDeviceId]);

  useEffect(() => {
    if (front || videoUrl || !showSecondaryCamera) {
      setSecondaryDeviceId(null);
      return;
    }

    let cancelled = false;
    const resolveDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        const videoInputs = devices.filter((d) => d.kind === "videoinput");
        if (!videoInputs.length) return;
        const currentPrimary = primaryDeviceId || iter?.cameraDeviceId || videoInputs[0].deviceId;
        const secondary = videoInputs.find((d) => d.deviceId !== currentPrimary)?.deviceId || null;
        if (!primaryDeviceId && currentPrimary) {
          setPrimaryDeviceId(currentPrimary);
        }
        setSecondaryDeviceId(secondary);
      } catch (error) {
        console.log("[BackendRecordingSide] camera enumeration failed", error);
      }
    };

    resolveDevices();
    return () => {
      cancelled = true;
    };
  }, [front, videoUrl, showSecondaryCamera, primaryDeviceId, iter?.cameraDeviceId]);

  useEffect(() => {
    if (!activeTrackingDeviceId) return;
    dispatch({
      type: "SET-ITER-CAMERA-DEVICE-ID",
      payload: activeTrackingDeviceId,
    });
  }, [activeTrackingDeviceId, dispatch]);

  // Visual-only: generate realistic wave-like leg flex/extension values for backend UX
  // Simulates actual gait cycle with alternating leg movement patterns
  useEffect(() => {
    if (front) return; // backend side view only
    if (!recordingEnabled && !apiProcessing) return;

    let timeCounter = 0;
    const gaitCycleDuration = 120; // 2 seconds at 60fps (200ms intervals)
    const baseAmplitude = 25; // Base flexion amplitude
    const noiseAmplitude = 3; // Small amount of noise for realism

    const generateGaitWave = (time, phaseOffset = 0) => {
      // Normalize time to 0-2π for full gait cycle
      const normalizedTime = ((time + phaseOffset) % gaitCycleDuration) / gaitCycleDuration * Math.PI * 2;
      
      // Create a more realistic gait pattern with multiple harmonics
      const mainWave = Math.sin(normalizedTime) * baseAmplitude;
      const secondaryWave = Math.sin(normalizedTime * 2) * (baseAmplitude * 0.3);
      const tertiaryWave = Math.sin(normalizedTime * 4) * (baseAmplitude * 0.1);
      
      // Add slight noise for realism
      const noise = (Math.random() - 0.5) * noiseAmplitude;
      
      // Combine waves and add some asymmetry typical in human gait
      const combinedWave = mainWave + secondaryWave + tertiaryWave + noise;
      
      // Clamp to realistic range (-35 to +35 degrees)
      return Math.max(-35, Math.min(35, combinedWave));
    };

    const id = setInterval(() => {
      setLegAngleData((prev) => {
        // clone shallow
        const next = prev.map((s) => ({ ...s, data: [...s.data] }));
        
        // Generate wave-like data for left and right legs with phase offset
        const leftAngle = generateGaitWave(timeCounter, 0);
        const rightAngle = generateGaitWave(timeCounter, Math.PI); // 180° phase offset
        
        // Generate related angle data for more realistic visualization
        const kneeAngleVariation = 0.6; // Knee angles vary less than hip angles
        const ankleAngleVariation = 0.4; // Ankle angles vary even less
        const hipAngleVariation = 0.8; // Hip angles are more pronounced
        
        // Left leg data
        if (next[0]) next[0].data.push(Number(leftAngle.toFixed(2))); // Left Leg Angle
        if (next[2]) next[2].data.push(Number((leftAngle * kneeAngleVariation).toFixed(2))); // Left Knee Angle
        if (next[4]) next[4].data.push(Number((leftAngle * ankleAngleVariation).toFixed(2))); // Left Ankle Angle
        if (next[6]) next[6].data.push(Number((leftAngle * hipAngleVariation).toFixed(2))); // Left Hip Angle
        
        // Right leg data
        if (next[1]) next[1].data.push(Number(rightAngle.toFixed(2))); // Right Leg Angle
        if (next[3]) next[3].data.push(Number((rightAngle * kneeAngleVariation).toFixed(2))); // Right Knee Angle
        if (next[5]) next[5].data.push(Number((rightAngle * ankleAngleVariation).toFixed(2))); // Right Ankle Angle
        if (next[7]) next[7].data.push(Number((rightAngle * hipAngleVariation).toFixed(2))); // Right Hip Angle
        
        // Hip rotation and balance angles (smaller variations)
        const rotationAngle = Math.sin(timeCounter * 0.05) * 8 + (Math.random() - 0.5) * 2;
        const balanceAngle = Math.cos(timeCounter * 0.03) * 5 + (Math.random() - 0.5) * 1.5;
        
        if (next[8]) next[8].data.push(Number(rotationAngle.toFixed(2))); // Hip Rotation Angle
        if (next[9]) next[9].data.push(Number(balanceAngle.toFixed(2))); // Hip Balance Angle
        
        // Keep memory bounded but allow for longer visualization
        for (let i = 0; i < next.length; i++) {
          if (next[i] && next[i].data.length > 500) {
            next[i].data.shift();
          }
        }
        
        timeCounter++;
        return next;
      });
    }, 200);

    return () => clearInterval(id);
  }, [front, recordingEnabled, apiProcessing]);

  useEffect(() => {
    resetGaitResults();
    addResults().then((res) => {
      if (res?.id) {
        setHasInitResult(true);
      } else {
        // reload the window
        window.location.reload();
      }
    });
  }, []);

  // Cleanup media streams on component unmount
  useEffect(() => {
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Enable button when video is saved to Firebase
  useEffect(() => {
    if (uploadedVideoUrl || recordedVideoUrl) {
      setButtonEnabled(true);
      setVideoSaved(true);
    } else {
      setButtonEnabled(false);
      setVideoSaved(false);
    }
  }, [uploadedVideoUrl, recordedVideoUrl]);

  useEffect(() => {
    if (front) {
      setObservationComponent(
        <GaitAnalysisFront
          setLegAngleData={setLegAngleData}
          showWebcam={showWebcam}
          recordingEnabled={recordingEnabled}
        />
      );
      setFeedbackComponent(<GaitFeedbacks front={true} />);
    } else {
      if (!videoUrl) {
        setObservationComponent(
          <GaitAnalysis
            key={`camGait-${activeTrackingDeviceId || "default"}`}
            setLegAngleData={setLegAngleData}
            showWebcam={showWebcam}
            videoUrl={videoUrl}
            sensorData={sensorData}
            recordingEnabled={recordingEnabled}
            relativeLandmarksRef={relativeLandmarks}
            useSensorForCycle={useSensorForCycle}
            poseInstanceKey={`pose-${activeTrackingDeviceId || "default"}`}
          />
        );
      } else {
        setObservationComponent(<> </>);
        setFeedbackComponent(<> </>);
        setVideoObservationComponent(
          <GaitAnalysis
            key={videoUrl}
            setLegAngleData={setLegAngleData}
            showWebcam={showWebcam}
            videoUrl={videoUrl}
            sensorData={sensorData}
            recordingEnabled={recordingEnabled}
            relativeLandmarksRef={relativeLandmarks}
            useSensorForCycle={useSensorForCycle}
          />
        );
      }
    }
  }, [
    showWebcam,
    front,
    recordingEnabled,
    videoUrl,
    showSecondaryCamera,
  ]);

  // Video recording functions
  const startVideoRecording = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 1280, height: 720 }, 
        audio: false 
      });

      const recorder = new MediaRecorder(mediaStream, {
        mimeType: 'video/webm;codecs=vp9',
      });

      const chunks = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        await uploadRecordedVideoToFirebase(blob);
        
        // Stop all tracks
        mediaStream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      mediaStreamRef.current = mediaStream;
      setIsRecording(true);
      setRecordingEnabled(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to start recording: ' + error.message);
    }
  };

  const stopVideoRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingEnabled(false);
    }
  };

  const uploadRecordedVideoToFirebase = async (blob) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const timestamp = Date.now();
      const fileName = `gait_analysis_recordings/${front ? 'front' : 'side'}_${timestamp}_recording.webm`;
      const storageRef = ref(storage, fileName);

      console.log('Uploading recorded video to Firebase...');
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) {
            return prev + Math.random() * 10 + 5;
          }
          return 90;
        });
      }, 200);

      await uploadBytes(storageRef, blob);
      
      const downloadURL = await getDownloadURL(storageRef);
      console.log('Recorded video available at:', downloadURL);
      
      setRecordedVideoUrl(downloadURL);
      clearInterval(progressInterval);
      setUploadProgress(100);
      setIsUploading(false);
    } catch (error) {
      console.error('Error uploading recorded video:', error);
      alert('Failed to upload recorded video: ' + error.message);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const resetRecordingState = () => {
    setIsRecording(false);
    setIsUploading(false);
    setUploadProgress(0);
    setRecordingEnabled(false);
    setVideoSaved(false);
    setRecordedVideoUrl("");
    setUploadedVideoUrl("");
    setVideoUrl("");
    setLegAngleData(initialData());
    resetGaitResults();
    
    // Stop any active media streams
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
  };

  const handleFinish = async () => {
    const finalVideoUrl = uploadedVideoUrl || recordedVideoUrl;
    console.log("Final video URL to send to backend:", finalVideoUrl);
    
    if (!finalVideoUrl) {
      alert("Please upload or record a video first!");
      return;
    }

    setLoading(true);
    setApiProcessing(true);
    setProcessingProgress(0);
    setProcessingStatus("Sending video to backend for analysis...");
    
    // Simulate progress from 0 to 80% slowly
    const progressInterval = setInterval(() => {
      setProcessingProgress(prev => {
        if (prev < 80) {
          return prev + Math.random() * 2 + 0.5; // Random increment between 0.5-2.5%
        }
        return 80; // Cap at 80%
      });
    }, 800);
    
    try {
      // Get patient height from Redux store
      const patientHeight = patientInfo.height;
      
      if (!patientHeight) {
        alert("Patient height is required. Please set patient information first.");
        setLoading(false);
        setApiProcessing(false);
        clearInterval(progressInterval);
        return;
      }

      console.log("Sending to backend API:", {
        video_url: finalVideoUrl,
        patient_height: patientHeight,
        patient_id: patientInfo.id,
        analysis_type: front ? "front_view" : "side_view"
      });

      setProcessingStatus("Processing video with AI analysis...");
      
      // Call the /analyze-gait-from-url endpoint with timeout handling
      const apiUrl = getApiEndpoint(BACKEND_CONFIG.ENDPOINTS.ANALYZE_GAIT_FROM_URL);
      const response = await createApiRequest(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          video_url: finalVideoUrl,
          patient_height: parseFloat(patientHeight), // Convert to number
          patient_id: patientInfo.id,
          analysis_type: front ? "front_view" : "side_view",
        }),
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      const backendResult = await response.json();
      console.log("Backend API response:", backendResult);
      console.log("🎯 BACKEND RESPONSE DEBUG - time_series_data:", backendResult.time_series_data);
      console.log("🎯 BACKEND RESPONSE DEBUG - Available keys:", Object.keys(backendResult));

      // Check if the backend analysis was successful
      if (backendResult.status !== 'success') {
        throw new Error(`Backend analysis failed: ${backendResult.error_message || 'Unknown error'}`);
      }

      // Import and use the transformer
      const { transformBackendGaitResponse, createBackendResultsPayload, validateBackendResponse } = await import(
        "../../../../../utilities/Demo/Physio/GaitAnalysis/backendDataTransformer"
      );

      // Validate the response structure
      validateBackendResponse(backendResult);

      setProcessingStatus("Transforming results for display...");
      
      // Transform backend response to frontend format
      const transformedData = transformBackendGaitResponse(backendResult);
      const resultsPayload = createBackendResultsPayload(transformedData);
      
      console.log("🎯 TRANSFORMED DATA DEBUG - resultsPayload:", resultsPayload);
      console.log("🎯 TRANSFORMED DATA DEBUG - resultsPayload.time_series_data:", resultsPayload.time_series_data);

      // Update Redux store with backend results
      const appendix = front ? "-FRONT" : "";
      dispatch({
        type: `UPDATE-GAIT${appendix}-OBSERVATION`,
        payload: transformedData.gaitData,
      });

      // Update results store
      dispatch({
        type: `UPDATE-GAIT-RESULTS`,
        payload: resultsPayload,
      });

      // 💾 CRITICAL: Save backend results to Firebase for persistence and history
      setProcessingStatus("Saving results to database...");
      try {
        console.log("💾 Saving backend results to Firebase...", {
          id: gaitResults.id,
          payloadKeys: Object.keys(resultsPayload),
          metricsPreview: {
            cadence: resultsPayload.metrics?.cadence,
            walkingSpeed: resultsPayload.metrics?.walkingSpeed,
            gaitCycleVariability: resultsPayload.metrics?.gaitCycleVariability,
            gaitCycleMeanOptimumDeviation: resultsPayload.metrics?.gaitCycleMeanOptimumDeviation,
          }
        });
        
        await updateResults({
          id: gaitResults.id,
          payload: resultsPayload,
        });
        
        console.log("✅ Backend results saved to Firebase successfully!");
        console.log("✅ Saved document ID:", gaitResults.id);
        console.log("✅ This document should now appear in history");
      } catch (saveError) {
        console.error("❌ Failed to save backend results to Firebase:", saveError);
        // Don't block navigation, but alert user
        alert("Warning: Results were processed but may not be saved to history. Please check your connection.");
      }

      // Complete the progress bar
      clearInterval(progressInterval);
      setProcessingProgress(100);
      
      setLoading(false);
      setApiProcessing(false);
      setProcessingStatus("");

      // Navigate to results page to show processed data
      if (!front) {
        navigate("/gait_result", {
          replace: false,
        });
      } else {
        setFront(false);
        navigate("/gait_analysis/backend", {
          replace: false,
        });
      }

    } catch (error) {
      console.error("Error processing video with backend API:", error);
      clearInterval(progressInterval);
      setProcessingProgress(0);
      alert(`Error processing video: ${error.message}`);
      setLoading(false);
      setApiProcessing(false);
      setProcessingStatus("");
    }
  };

  if (!hasInitResult) {
    return <CircularProgress />;
  }

  return (
    <div
      style={{
        overflow: "hidden",
      }}
    >
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sx={{
            backgroundColor: "red",
          }}
        >
          {" "}
          {(videoUrl || recordedVideoUrl || uploadedVideoUrl) && (
            <IconButton
              onClick={() => {
                if (videoUrl) {
                  URL.revokeObjectURL(videoUrl);
                }
                resetRecordingState();
                setVideoObservationComponent(<> </>);
              }}
              style={{
                color: "black",
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}{" "}
        </Grid>{" "}
        <Grid
          item
          xs={6}
          sm={8}
          style={{
            position: "relative",
            height: "100%",
          }}
        >
          <div
            style={{
              position: "relative",
              overflow: "visible",
              backgroundColor: "black",
              height: showSecondaryCamera && secondaryDeviceId && !videoUrl ? "auto" : "100%",
              borderRadius: 20,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {" "}
            <div style={{ position: "relative", width: "100%", minHeight: 360, height: "100%" }}>
              {!videoUrl && observationComponent} {videoObservationComponent}{" "}
            </div>
            {showSecondaryCamera && secondaryDeviceId && !videoUrl && (
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  minHeight: 360,
                  borderTop: "1px solid rgba(255,255,255,0.2)",
                  backgroundColor: "black",
                }}
              >
                <SecondaryCamera
                  key={secondaryDeviceId}
                  deviceId={secondaryDeviceId}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: showWebcam ? 1 : 0,
                  }}
                />
              </div>
            )}
            {feedbackComponent}{" "}
          </div>{" "}
        </Grid>{" "}
        <Grid item xs={5.5} sm={3.5}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              backgroundColor: "white",
              height: "100%",
            }}
          >
            {!recordingEnabled && !isRecording && !videoSaved && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  gap: 2,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<VideocamIcon />}
                  onClick={startVideoRecording}
                  disabled={isUploading}
                  sx={{ minWidth: 200 }}
                >
                  Start Recording
                </Button>
                
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  OR
                </Typography>
                
                <BackendVideoSelector
                  setVideoUrl={(val) => {
                    setVideoUrl(val);
                    setRecordingEnabled(true);
                  }}
                  videoUrl={videoUrl}
                  setUploadedVideoUrl={setUploadedVideoUrl}
                />
              </Box>
            )}

            {isRecording && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "error.main",
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: "error.main",
                      animation: "pulse 1s infinite",
                      "@keyframes pulse": {
                        "0%": { opacity: 1 },
                        "50%": { opacity: 0.3 },
                        "100%": { opacity: 1 },
                      },
                    }}
                  />
                  <Typography variant="h6" color="error.main">
                    Recording...
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={stopVideoRecording}
                  sx={{ minWidth: 200 }}
                >
                  Stop Recording
                </Button>
              </Box>
            )}

            {isUploading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  gap: 2,
                }}
              >
                <CircularProgress size={40} />
                <Typography variant="h6" color="primary">
                  Uploading Video...
                </Typography>
                <Box sx={{ width: "100%", maxWidth: 200 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                    {Math.round(uploadProgress)}%
                  </Typography>
                </Box>
              </Box>
            )}

            {videoSaved && !isUploading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  gap: 2,
                }}
              >
                <Typography variant="h6" color="success.main" sx={{ textAlign: 'center' }}>
                  Video Ready for Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Click "SEND TO BACKEND" to start analysis
                </Typography>
              </Box>
            )}{" "}
          </Box>{" "}
        </Grid>{" "}
        <Grid
          container
          spacing={1}
          sx={{
            margin: 1,
          }}
        >
          <Grid item xs={8} sm={8}>
            {apiProcessing && (
              <Box
                sx={{
                  borderRadius: 3,
                  padding: 3,
                  height: "100%",
                  width: "100%",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3,
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Animated background pattern */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)",
                    animation: "float 6s ease-in-out infinite",
                    "@keyframes float": {
                      "0%, 100%": { transform: "translateY(0px)" },
                      "50%": { transform: "translateY(-10px)" },
                    },
                  }}
                />
                
                {/* Main content */}
                <Box sx={{ textAlign: "center", zIndex: 1 }}>
                  {/* AI Icon/Animation */}
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "rgba(255, 255, 255, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                      animation: "pulse 2s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { transform: "scale(1)" },
                        "50%": { transform: "scale(1.05)" },
                      },
                    }}
                  >
                    <CircularProgress 
                      size={40} 
                      thickness={3}
                      sx={{ 
                        color: "white",
                        "& .MuiCircularProgress-circle": {
                          strokeLinecap: "round",
                        }
                      }} 
                    />
                  </Box>
                  
                  {/* Status Text */}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: "white", 
                      fontWeight: 600,
                      mb: 1,
                      textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                    }}
                  >
                    AI Analysis in Progress
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: "rgba(255, 255, 255, 0.8)", 
                      mb: 3,
                      fontSize: "0.9rem",
                    }}
                  >
                    {processingStatus}
                  </Typography>
                </Box>
                
                {/* Progress Section */}
                <Box sx={{ width: "100%", zIndex: 1 }}>
                  {/* Progress Bar Container */}
                  <Box
                    sx={{
                      width: "100%",
                      height: 12,
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      borderRadius: 6,
                      overflow: "hidden",
                      position: "relative",
                      mb: 2,
                    }}
                  >
                    {/* Progress Fill */}
                    <Box
                      sx={{
                        height: "100%",
                        width: `${processingProgress}%`,
                        background: "linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)",
                        borderRadius: 6,
                        transition: "width 0.3s ease",
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)",
                          animation: "shimmer 2s ease-in-out infinite",
                          "@keyframes shimmer": {
                            "0%": { transform: "translateX(-100%)" },
                            "100%": { transform: "translateX(100%)" },
                          },
                        },
                      }}
                    />
                  </Box>
                  
                  {/* Percentage Display */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: "rgba(255, 255, 255, 0.9)",
                        fontWeight: 500,
                      }}
                    >
                      Processing...
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: "white",
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      {Math.round(processingProgress)}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {!apiProcessing && !videoSaved && (
              <Box
                sx={{
                  borderColor: "black",
                  borderRadius: 1,
                  border: 1,
                  paddingLeft: 2,
                  height: "100%",
                  width: "100%",
                }}
              >
                <LinearWithValueLabel front={front} />{" "}
              </Box>
            )}
          </Grid>{" "}
          <Grid
            item
            xs={4}
            sm={3.8}
            sx={{
              display: "flex",
              justifyContent: "right",
              height: "100%",
              width: "100%",
              marginRight: 0,
            }}
          >
            <LoadingButton
              fullWidth
              loading={loading}
              sx={{
                borderRadius: "80",
              }}
              variant={"outlined"}
              disabled={!buttonEnabled || apiProcessing}
              onClick={handleFinish}
            >
              {" "}
              {!front ? "SEND TO BACKEND" : "OBSERVE FRONT"}{" "}
            </LoadingButton>{" "}
          </Grid>{" "}
        </Grid>{" "}
      </Grid>{" "}
      <Box
        sx={{
          marginTop: 2,
          width: "100%",
        }}
      >
        <Grid container spacing={2}>
          <Grid
            item
            xs={6}
            sm={3.4}
            sx={{
              display: "flex",
              justifyContent: "right",
              alignItems: "right",
            }}
          >
            <Typography
              sx={{
                mt: 1,
              }}
            >
              {" "}
              {"Hide Webcam Video"}{" "}
            </Typography>{" "}
            <Switch
              onClick={() => {
                setShowWebcam(!showWebcam);
              }}
            />{" "}
          </Grid>{" "}
          <Grid
            item
            xs={6}
            sm={3.4}
            sx={{
              display: "flex",
              justifyContent: "left",
              alignItems: "right",
            }}
          >
            <Typography
              sx={{
                mt: 1,
              }}
            >
              {"Show Secondary Camera"}{" "}
            </Typography>{" "}
            <Switch
              checked={showSecondaryCamera}
              onChange={(e) => {
                setShowSecondaryCamera(e.target.checked);
              }}
            />{" "}
          </Grid>{" "}
        </Grid>{" "}
        {/* Stridalizer removed for gait analysis */}
        {/* Removed sensor toggle for gait analysis */}
      </Box>{" "}
    </div>
  );
}
